import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromMembers from './reducers/members.reducer';
import * as fromRoot from '@app/reducers';
import { Member } from '@app/shared/models/member';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { MembersActions } from './actions';
import { ModalAddMemberComponent } from './modals/modal-add-member/modal-add-member.component';
import { ModalDeleteMemberComponent } from './modals/modal-delete-member/modal-delete-member.component';
import { ModalImportMembersComponent } from './modals/modal-import-members/modal-import-members.component';
import { User } from '@app/shared/models/user';
import { first, take, map } from 'rxjs/operators';
import { Role } from '@app/shared/models/role';
import { Identity } from '@app/shared/models/identity';
import { Column } from '@app/shared/modules/smash-data-table/smash-data-table/smash-data-table.component';

@Component({
  selector: 'app-members',
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.scss']
})
export class MembersComponent implements OnInit, OnDestroy {

  identity$: Observable<Identity>;

  members$: Observable<any[]>;
  membersLoading$: Observable<boolean>;
  csvExportData$: Observable<any[]>;

  accountMembers$: Observable<User[]>;
  accountMembersLoading$: Observable<boolean>;
  accountMembersLoaded$: Observable<boolean>;

  teamRoles$: Observable<Role[]>;
  teamRolesLoading$: Observable<boolean>;
  teamRolesLoaded$: Observable<boolean>;

  subscriptions: Subscription[] = [];
  disabled = false;
  memberToDelete: User;
  search: any;
  teamId: string;

  columnsEnabled: Column[] = [
    { id: 'firstName', colName: 'PREMIUM_ADMIN_MEMBERS_TABLE_LABEL_FIRSTNAME', type: 'string', removable: false, searchable: true },
    { id: 'username', colName: 'PREMIUM_ADMIN_MEMBERS_TABLE_LABEL_EMAIL', type: 'string', removable: false, searchable: true },
    {
      id: 'status', colName: 'PREMIUM_ADMIN_MEMBERS_TABLE_LABEL_STATUS', type: 'string', removable: false,
      translations: {
        Enabled: 'PREMIUM_ADMIN_MEMBERS_TABLE_LABEL_MEMBER_ENABLED',
        Disabled: 'PREMIUM_ADMIN_MEMBERS_TABLE_LABEL_MEMBER_DISABLED',
      }
    },
  ];

  constructor(private store$: Store<fromRoot.State & fromMembers.State>, private router: Router, private route: ActivatedRoute, private dialog: DialogService) {
    this.refreshMembers();
    this.refreshTeamRoles();
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);

    this.members$ = this.store$.select(fromMembers.getMembers);
    this.membersLoading$ = this.store$.select(fromMembers.getMembersLoading);

    // Needed for columns order
    this.csvExportData$ = this.members$.pipe(
      map(members => members?.map((member: User) => ({
        lastName: member.lastName,
        firstName: member.firstName,
        username: member.username,
        language: member.language,
        region: member.region,
        status: member.status,
        authenticationMode: member.authenticationMode,
        provisioned: member.provisioned,
        created: member.created,
        modified: member.modified,
        id: member.id
      })) || [])
    );

    this.accountMembers$ = this.store$.select(fromMembers.getAccountMembers);
    this.accountMembersLoading$ = this.store$.select(fromMembers.getAccountMembersLoading);
    this.accountMembersLoaded$ = this.store$.select(fromMembers.getAccountMembersLoaded);

    this.teamRoles$ = this.store$.select(fromMembers.getTeamRoles);
    this.teamRolesLoading$ = this.store$.select(fromMembers.getTeamRolesLoading);
    this.teamRolesLoaded$ = this.store$.select(fromMembers.getTeamRolesLoaded);
  }

  ngOnInit(): void { }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
  }

  viewMemberDetails(id: string) {
    this.router.navigateByUrl('/account/team/' + this.teamId + '/members/' + id);
  }

  inviteMemberDetails(userId: string) {
    this.route.params.pipe(take(1)).subscribe(({ id }) => {
      this.store$.dispatch(MembersActions.inviteMember({ teamId: id, userId }));
    });
  }

  openModalDeleteMember(member: Member) {
    this.dialog.open(ModalDeleteMemberComponent, {
      enableClose: true,
      closeButton: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        memberToDelete: member
      }
    });
    event.stopPropagation();
    return false;
  }

  async openModalAddMember() {
    this.store$.dispatch(MembersActions.resetCreation());
    const accountMembers = await this.accountMembers$.pipe(first(members => !!members)).toPromise();
    const teamMembers = await this.members$.pipe(first(members => !!members)).toPromise();
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    const attachableMembers = accountMembers.filter(accountMember => !teamMembers.some(teamMember => accountMember.id === teamMember.id));
    const attachableMembersFiltered = attachableMembers.filter(attachableMember => attachableMember.id !== identity.id);
    const teamRoles = await this.teamRoles$.pipe(first(roles => !!roles)).toPromise();
    this.dialog.open(ModalAddMemberComponent, {
      closeButton: false,
      enableClose: false,
      size: 'sm',
      data: {
        teamId: this.teamId,
        attachableMembers: attachableMembersFiltered,
        teamRoles
      }
    });
    event.stopPropagation();
    return false;
  }

  openModalImportMembers() {
    this.store$.dispatch(MembersActions.resetCreation());
    this.dialog.open(ModalImportMembersComponent, {
      closeButton: false,
      enableClose: false,
      size: 'xl',
      data: {
        teamId: this.teamId,
      }
    });
    event.stopPropagation();
    return false;
  }

  refreshMembers(): void {
    this.route.params.pipe(take(1)).subscribe(({ id }) => {
      this.teamId = id;
      this.store$.dispatch(MembersActions.loadMembers({ teamId: this.teamId }));
      this.store$.dispatch(MembersActions.LoadAccountMembers());
    });
  }

  refreshTeamRoles(): void {
    this.store$.dispatch(MembersActions.loadTeamRoles({ teamId: this.teamId }));
  }
}
